/**
 * Turning a chosen file into something safe to put in an event's Banner.
 *
 * ## Why a data URI, and why no upload endpoint is called
 *
 * There is no banner upload route: `event/admin/banners/` is read-only and
 * nothing writes to the `Banners` collection. There does not need to be. On
 * write, `update_event_admin` runs `lift_document` over the WHOLE payload, and
 * that walks every field looking for a bare `data:` URI, stores what it finds in
 * blob storage, and replaces it with a reference. On read, global middleware
 * signs every reference in any JSON response back into a usable URL. So a data
 * URI in `Banner.ImageUrl` is uploaded, stored and served without a line of
 * server code — the same path the rich-text fields already use.
 *
 * ## Why the file is re-encoded rather than read straight through
 *
 * Because it travels inside the event's PUT body, and base64 adds a third again
 * on top of whatever it is given. A phone photo is 4MB before encoding and 5.5MB
 * after, in a JSON body, on every subsequent save of that event until the first
 * one lands and the reference replaces it. There is no size cap in the API's
 * image lifting, so the guard has to be here.
 *
 * `CCRichText` in this app refuses dropped images outright for exactly this
 * reason. A banner is worth the trouble that a paragraph image was not, but the
 * trouble is real, hence the cap and the downscale below.
 */

/** Longest edge kept. A banner is never displayed larger than this. */
const MAX_EDGE = 1600;

/** JPEG quality. 0.82 is where a photo stops getting visibly better per byte. */
const QUALITY = 0.82;

/** Refused before reading, so a video renamed `.jpg` never reaches a decoder. */
const MAX_INPUT_BYTES = 12 * 1024 * 1024;

/**
 * The encoded result must still be sane. Downscaling makes this unreachable in
 * practice; it is here so that a pathological image cannot put 30MB of base64
 * into a PUT body.
 */
const MAX_ENCODED_BYTES = 3 * 1024 * 1024;

export const ACCEPTED_IMAGE_TYPES = "image/png,image/jpeg,image/webp";

const ACCEPTED = new Set(["image/png", "image/jpeg", "image/webp"]);

/** Thrown with a message written to be shown to the person who picked the file. */
export class BannerImageError extends Error {}

/**
 * A chosen file as a downscaled JPEG data URI, ready to be saved as a banner.
 *
 * FLATTENED ONTO WHITE. JPEG has no alpha, and a transparent PNG drawn onto a
 * bare canvas comes out with black where it was see-through, which looks like a
 * corrupted image rather than a design choice. White is what these sit on.
 */
export async function bannerDataUrl(file: File): Promise<string> {
	if (!ACCEPTED.has(file.type)) {
		throw new BannerImageError("Choose a JPEG, PNG or WebP image.");
	}
	if (file.size > MAX_INPUT_BYTES) {
		throw new BannerImageError(
			`That image is ${Math.round(file.size / 1024 / 1024)}MB. Please choose one under 12MB.`,
		);
	}

	let bitmap: ImageBitmap;
	try {
		/* `from-image` so a photo taken in portrait is not stored on its side:
		   browsers vary on whether EXIF orientation is applied by default. */
		bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
	} catch {
		throw new BannerImageError("That file could not be read as an image.");
	}

	try {
		const scale = Math.min(1, MAX_EDGE / Math.max(bitmap.width, bitmap.height));
		const width = Math.max(1, Math.round(bitmap.width * scale));
		const height = Math.max(1, Math.round(bitmap.height * scale));

		const canvas = document.createElement("canvas");
		canvas.width = width;
		canvas.height = height;
		const ctx = canvas.getContext("2d");
		if (!ctx) throw new BannerImageError("That image could not be processed.");

		ctx.fillStyle = "#ffffff";
		ctx.fillRect(0, 0, width, height);
		ctx.drawImage(bitmap, 0, 0, width, height);

		const dataUrl = canvas.toDataURL("image/jpeg", QUALITY);
		/* Base64 is 4 characters per 3 bytes, so the string length is a close
		   enough measure of what goes over the wire. */
		if (dataUrl.length > MAX_ENCODED_BYTES) {
			throw new BannerImageError(
				"That image is too detailed to store. Please choose another.",
			);
		}
		return dataUrl;
	} finally {
		bitmap.close();
	}
}
